import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { PriorityBadge, PRIORITY_OPTIONS } from "./priority-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TaskPrioritySelectProps {
  taskId: string;
  currentPriority: string | null;
  canEdit: boolean;
  hideLabel?: boolean;
}

export function TaskPrioritySelect({ taskId, currentPriority, canEdit, hideLabel }: TaskPrioritySelectProps) {
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: async (priority: string | null) =>
      apiRequest("PATCH", `/api/tasks/${taskId}`, { priority }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) =>
      toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  if (!canEdit) {
    if (!currentPriority) return null;
    return (
      <div className="flex items-center gap-1.5">
        {!hideLabel && <span className="text-xs text-muted-foreground">Priority:</span>}
        <PriorityBadge priority={currentPriority} />
      </div>
    );
  }

  const currentLabel = PRIORITY_OPTIONS.find(o => o.value === (currentPriority ?? "none"))?.label ?? "None";

  return (
    <div className="flex items-center gap-1.5">
      {!hideLabel && <Label className="text-xs text-muted-foreground whitespace-nowrap">Priority:</Label>}
      <Select
        value={currentPriority ?? "none"}
        onValueChange={v => updateMutation.mutate(v === "none" ? null : v)}
        disabled={updateMutation.isPending}
      >
        <SelectTrigger className="h-7 w-auto min-w-[90px] text-sm gap-1" data-testid="task-select-priority-inline">
          <SelectValue>{currentLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PRIORITY_OPTIONS.map(o => (
            <SelectItem key={o.value} value={o.value} data-testid={`option-priority-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
